import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  LayoutDashboard,
  Copy,
  Trash2,
  Download,
  ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function Dashboards() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", company_id: "" });
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, companiesRes] = await Promise.all([
        api.get("/dashboards"),
        isAdmin ? api.get("/companies").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setItems(list);
      setCompanies(companiesRes.data || []);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  async function create() {
    if (!form.name) return toast.error("Name is required");
    try {
      const payload = { name: form.name, description: form.description };
      if (isAdmin && form.company_id) payload.company_id = form.company_id;
      const { data } = await api.post("/dashboards", payload);
      setShowCreate(false);
      setForm({ name: "", description: "", company_id: "" });
      navigate(`/dashboards/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function duplicate(id) {
    try {
      const { data } = await api.post(`/dashboards/${id}/duplicate`);
      toast.success("Dashboard duplicated");
      setItems((prev) => [data, ...prev]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this dashboard and its charts/alarms?")) return;
    try {
      await api.delete(`/dashboards/${id}`);
      setItems((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function exportDash(id, name, fmt) {
    try {
      const res = await api.get(`/dashboards/${id}/export`, {
        params: { fmt },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.warn("export failed:", e?.message);
      toast.error("Export failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="dashboards-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="kpi-label">{t("nav.dashboards")}</p>
          <h1 className="text-2xl font-bold tracking-tight">{t("dash.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t(items.length === 1 ? "dash.subtitle_one" : "dash.subtitle_many", { n: items.length })}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} data-testid="new-dashboard-btn">
            <Plus className="h-4 w-4 mr-1.5" /> {t("dash.new")}
          </Button>
        )}
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : items.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)} canCreate={isAdmin} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((d) => (
            <Card
              key={d.id}
              className="card-soft cursor-pointer group"
              onClick={() => navigate(`/dashboards/${d.id}`)}
              data-testid={`dashboard-card-${d.id}`}
            >
              <CardContent className="p-5 flex flex-col h-full">
                <div className="flex items-start justify-between">
                  <div className="h-9 w-9 rounded-md bg-primary/10 grid place-items-center">
                    <LayoutDashboard className="h-4 w-4 text-primary" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`dashboard-menu-${d.id}`}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {isAdmin && (
                        <>
                          <DropdownMenuItem onClick={() => duplicate(d.id)}>
                            <Copy className="h-4 w-4 mr-2" /> {t("dash.duplicate")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem onClick={() => exportDash(d.id, d.name, "csv")}>
                        <Download className="h-4 w-4 mr-2" /> {t("dash.export_csv")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportDash(d.id, d.name, "xlsx")}>
                        <Download className="h-4 w-4 mr-2" /> {t("dash.export_xlsx")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportDash(d.id, d.name, "pdf")}>
                        <Download className="h-4 w-4 mr-2" /> {t("dash.export_pdf")}
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => remove(d.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> {t("common.delete")}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="mt-4 font-semibold text-base group-hover:text-primary transition-colors">
                  {d.name}
                </h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2 min-h-[2rem]">
                  {d.description || t("dash.no_description")}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                    {new Date(d.created_at).toLocaleDateString()}
                  </Badge>
                  <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Open →
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="new-dashboard-dialog">
          <DialogHeader>
            <DialogTitle>{t("dash.dialog_title")}</DialogTitle>
            <DialogDescription>{t("dash.dialog_subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="dn">{t("common.name")}</Label>
              <Input
                id="dn"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-dashboard-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dd">{t("common.description")}</Label>
              <Textarea
                id="dd"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
            {isAdmin && (
              <div className="space-y-1.5">
                <Label>{t("common.company")}</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger><SelectValue placeholder={t("chart.choose")} /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button onClick={create} data-testid="new-dashboard-create">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ onCreate, canCreate }) {
  const t = useT();
  return (
    <Card className="card-soft">
      <CardContent className="py-16 flex flex-col items-center text-center">
        <div className="h-12 w-12 rounded-full bg-primary/10 grid place-items-center mb-4">
          <LayoutDashboard className="h-5 w-5 text-primary" />
        </div>
        <h3 className="font-semibold">{t("dash.empty_title")}</h3>
        <p className="text-sm text-muted-foreground max-w-sm mt-1">
          {canCreate ? t("dash.empty_admin") : t("dash.empty_user")}
        </p>
        {canCreate && (
          <Button className="mt-5" onClick={onCreate} data-testid="empty-create-dashboard">
            <Plus className="h-4 w-4 mr-1.5" /> {t("dash.create_dashboard")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={`dash-skel-${i}`} className="card-soft">
          <CardContent className="p-5">
            <div className="h-9 w-9 rounded-md bg-muted animate-pulse" />
            <div className="h-4 w-2/3 bg-muted rounded mt-4 animate-pulse" />
            <div className="h-3 w-1/2 bg-muted rounded mt-2 animate-pulse" />
            <div className="h-3 w-1/3 bg-muted rounded mt-6 animate-pulse" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
